import { inject, injectable } from "inversify";
import React from "react";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { UserStore } from "../../../../services/UserStore";
import { UrlStore } from "../../../../services/urlStore";
import { OrganizationStore } from "../../../../services/OrganizationStore";
import { FiltersStore } from "../../../../services/filtersStore";
import { OrgConfigsStore } from "../../../../services/OrgConfigsStore";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { deleteJSON, getJSON, postJSON } from "@whatslly/commons/apiUtils";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { TFunction } from "i18next";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { GenerateCertificateResponse } from "@whatslly/types/messages-server/destinations";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";

export type ArchiveTeamsSettingsFormData = {
  clientId: string;
  tenantId: string;
};

interface ArchivePlatformRecord {
  _id: string;
  orgId: string;
  platform: string;
  disabled?: boolean;
  selector: string;
  subscriptionId?: string;
  subscriptionExpiresAt?: string;
  config: {
    tenantId: string;
    clientId: string;
  };
}

@injectable()
export class ArchiveTeamsSettingsStore {
  isInitialized = false;
  connected = false;
  isConnecting = false;
  teamsArchiveEnabled = false;
  platform?: ArchivePlatformRecord;
  downloadCertificatePromise: IPromiseBasedObservable<GenerateCertificateResponse | void> | undefined;

  constructor(
    @inject(InjectUserStore) private readonly userStore: UserStore,
    @inject(FiltersStore) private readonly filtersStore: FiltersStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(OrganizationStore) private readonly orgStore: OrganizationStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      isInitialized: observable,
      connected: observable,
      isConnecting: observable,
      teamsArchiveEnabled: observable,
      platform: observable,
      downloadCertificatePromise: observable,
      isTeamsArchiveEnabled: computed,
      isReadOnlySettingsUser: computed,
      filters: computed,
      init: action.bound,
      downloadCertificate: action,
      certificateData: computed,
    });

    reaction(
      () => this.downloadCertificatePromise?.state,
      () => {
        if (this.downloadCertificatePromise?.state === "fulfilled" && this.downloadCertificatePromise.value?.success === true) {
          downloadBlob(atob(this.downloadCertificatePromise.value.certificate), "tuvis-archive-teams-cert.pem", "application/x-pem-file");
        }
      }
    );
    this.init();
  }

  get isTeamsArchiveEnabled() {
    return this.teamsArchiveEnabled;
  }

  get isReadOnlySettingsUser() {
    return !this.isTeamsArchiveEnabled || !hasPermission(this.userStore.user, UserPermissions["archive.update_settings"]);
  }

  get orgId() {
    return this.filtersStore.orgId || this.userStore.user?.organizationId;
  }

  get filters() {
    return { orgId: this.orgId ?? "" };
  }

  async init() {
    const teamsArchiveEnabled = await this.orgConfigsStore.getObservableConfig("allow_teams_archive").then(
      (value) => value,
      (err) => this.logger.error({ err }, "Failed to load allow_teams_archive config")
    );
    runInAction(() => {
      this.teamsArchiveEnabled = Boolean(teamsArchiveEnabled);
    });
    if (!this.isTeamsArchiveEnabled || !hasPermission(this.userStore.user, UserPermissions["archive.view_settings"])) {
      runInAction(() => {
        this.connected = false;
        this.isInitialized = true;
      });
      return;
    }

    try {
      const res = await getJSON({
        urlSuffix: `/archive/${this.orgId}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken,
      }).then((res) => res.json());

      runInAction(() => {
        if (res.success && Array.isArray(res.platforms)) {
          const teamsPlatform = res.platforms.find((p: ArchivePlatformRecord) => p.platform === "teams" && !p.disabled);
          this.connected = !!teamsPlatform;
          this.platform = this.connected ? teamsPlatform : undefined;
          this.isInitialized = true;
        } else {
          this.connected = false;
          this.isInitialized = true;
        }
      });
    } catch (err: any) {
      this.logger.error({ err }, "Failed to check Archive Teams connection status");
      runInAction(() => {
        this.connected = false;
        this.isInitialized = true;
      });
    }
  }

  get certificateData() {
    if (this.downloadCertificatePromise?.state !== "fulfilled" || !this.downloadCertificatePromise.value?.success) return undefined;
    return {
      privateKey: this.downloadCertificatePromise.value.privateKey,
      certificate: this.downloadCertificatePromise.value.certificate,
    };
  }

  private async getCertificateData(t: TFunction) {
    try {
      const res = await getJSON({
        urlSuffix: `/message/destinations/${await this.orgStore.orgId}/generate-certificate`,
        etoken: this.userStore.etoken ?? "",
      });
      return parseOrReturnError<GenerateCertificateResponse>(res, this.logger);
    } catch {
      modalService.openModal(Toast, { text: t("archive_teams_settings_page.download_certificate_failed"), type: "error" });
    }
  }

  async downloadCertificate(t: TFunction) {
    this.downloadCertificatePromise = fromPromise(this.getCertificateData(t));
  }

  async connectArchiveTeams(t: TFunction, formData: ArchiveTeamsSettingsFormData) {
    runInAction(() => {
      this.isConnecting = true;
    });

    try {
      if (!this.certificateData) {
        throw new Error(t("archive_teams_settings_page.certificate_required"));
      }

      const res = await postJSON({
        urlSuffix: `/archive/${this.orgId}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken ?? "",
        body: JSON.stringify({
          platform: "teams",
          config: {
            tenantId: formData.tenantId,
            clientId: formData.clientId,
            privateKey: this.certificateData.privateKey,
            certificate: this.certificateData.certificate,
          },
          selector: "all",
        }),
      }).then((res) => res.json());

      if (!res.success) {
        throw new Error(res.error || t("archive_teams_settings_page.connection_failed"));
      }

      runInAction(() => {
        this.isConnecting = false;
      });
      modalService.openModal(Toast, { text: t("archive_teams_settings_page.connected_success"), type: "regular" });
      this.init();
    } catch (err: any) {
      runInAction(() => {
        this.isConnecting = false;
      });
      modalService.openModal(Toast, { text: err.message || t("archive_teams_settings_page.connection_failed"), type: "error" });
    }
  }

  async disconnect() {
    if (!this.platform?._id) return;

    await deleteJSON({
      urlSuffix: `/archive/${this.orgId}/${this.platform._id}`,
      etoken: this.userStore.etoken ?? "",
      pod: this.userStore.pod,
      body: JSON.stringify({}),
    });

    this.init();
  }

  async openUninstallModal(t: TFunction) {
    const modal = modalService.openModal(ConfirmModal, {
      modalType: "warning",
      title: t("archive_teams_settings_page.uninstall_modal.title"),
      body: (
        <>
          <p>{t("archive_teams_settings_page.uninstall_modal.description")}</p>
          <p style={{ fontWeight: 600 }}>{t("archive_teams_settings_page.uninstall_modal.warning")}</p>
        </>
      ),
      okText: t("archive_teams_settings_page.uninstall_modal.confirm"),
      cancelText: t("archive_teams_settings_page.uninstall_modal.cancel"),
      testId: "uninstall-archive-teams-modal",
    });
    const result = await modal.promise;

    if (result?.result) {
      try {
        await this.disconnect();
        modalService.openModal(Toast, {
          text: t("archive_teams_settings_page.uninstall_modal.success"),
          type: "regular",
        });
      } catch {
        modalService.openModal(Toast, {
          text: t("archive_teams_settings_page.uninstall_modal.error"),
          type: "error",
        });
      }
    }
  }
}
